import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useAccentColor } from '../../contexts/AccentColorContext';

interface SidebarProps {
  onCollapse?: (collapsed: boolean) => void;
}

const EXTERNAL_ROLES = ['agent_declarant', 'lecteur', 'collaborateur_national', 'collaborateur_international'];
const CENTIF_ROLES = ['admin', 'superviseur', 'analyste', 'enqueteur'];

function getTheme(role: string | undefined) {
  if (!role) return { bg: '#2374AB', active: 'rgba(255,255,255,0.18)', hover: 'rgba(255,255,255,0.10)', text: '#ffffff', subtext: 'rgba(255,255,255,0.65)', border: 'rgba(255,255,255,0.15)' };
  if (EXTERNAL_ROLES.includes(role)) {
    return {
      bg: '#0D7A6F',
      active: 'rgba(255,255,255,0.18)',
      hover: 'rgba(255,255,255,0.10)',
      text: '#ffffff',
      subtext: 'rgba(255,255,255,0.65)',
      border: 'rgba(255,255,255,0.15)',
    };
  }
  return {
    bg: '#2374AB',
    active: 'rgba(255,255,255,0.18)',
    hover: 'rgba(255,255,255,0.10)',
    text: '#ffffff',
    subtext: 'rgba(255,255,255,0.65)',
    border: 'rgba(255,255,255,0.15)',
  };
}

function getDefaultPinnedPaths(role: string | undefined): string[] {
  if (!role) return [];
  
  const pinnedByRole: Record<string, string[]> = {
    analyste: ['/mon-espace', '/dossiers', '/suivi-delais-72h'],
    superviseur: ['/superviseur', '/dossiers', '/statistiques-delais'],
    admin: ['/gestion-utilisateurs', '/administration', '/dashboard'],
    agent_declarant: ['/mon-espace', '/dossiers', '/demandes'],
    collaborateur_national: ['/mon-organisme', '/echanges-inter-organisations', '/suivi-reponses', '/notifications-alertes', '/gestion-documentaire'],
    collaborateur_international: ['/mon-organisme', '/echanges-inter-organisations', '/suivi-reponses', '/notifications-alertes', '/gestion-documentaire'],
    enqueteur: ['/espace-enqueteur', '/dossiers'],
    lecteur: ['/espace-lecteur', '/dossiers'],
  };
  
  return pinnedByRole[role] || [];
}

export default function Sidebar({ onCollapse }: SidebarProps) {
  const location = useLocation();
  const { user } = useAuth();
  const { accentColor } = useAccentColor();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showManageFavorites, setShowManageFavorites] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [favorites, setFavorites] = useState<string[]>([]);

  const theme = getTheme(user?.role);

  // Charger les favoris depuis localStorage
  useEffect(() => {
    if (!user?.id) return;
    
    const storageKey = `sidebar_favorites_${user.id}`;
    const savedFavorites = localStorage.getItem(storageKey);
    
    if (savedFavorites) {
      try {
        setFavorites(JSON.parse(savedFavorites));
      } catch (e) {
        // Si erreur de parsing, initialiser avec les favoris par défaut
        const defaultFavorites = getDefaultPinnedPaths(user.role);
        setFavorites(defaultFavorites);
        localStorage.setItem(storageKey, JSON.stringify(defaultFavorites));
      }
    } else {
      // Première connexion : initialiser avec les favoris par défaut
      const defaultFavorites = getDefaultPinnedPaths(user.role);
      setFavorites(defaultFavorites);
      localStorage.setItem(storageKey, JSON.stringify(defaultFavorites));
    }
  }, [user?.id, user?.role]);

  // Sauvegarder les favoris dans localStorage
  const saveFavorites = (newFavorites: string[]) => {
    if (!user?.id) return;
    const storageKey = `sidebar_favorites_${user.id}`;
    localStorage.setItem(storageKey, JSON.stringify(newFavorites));
    setFavorites(newFavorites);
  };

  // Toggle favori
  const toggleFavorite = (path: string) => {
    const newFavorites = favorites.includes(path)
      ? favorites.filter(f => f !== path)
      : [...favorites, path];
    saveFavorites(newFavorites);
  };

  const getMenuItemsForRole = () => {
    const allMenuItems = [
      { path: '/dashboard', label: 'Tableau de bord', icon: 'ri-dashboard-line', roles: ['admin', 'superviseur', 'analyste', 'enqueteur', 'agent_declarant', 'collaborateur_national', 'collaborateur_international', 'lecteur'] },
      { path: '/mon-espace', label: 'Mon Espace', icon: 'ri-user-heart-line', roles: ['admin','analyste', 'enqueteur', 'superviseur'] },
      { path: '/superviseur', label: 'Superviseur CDAS', icon: 'ri-team-line', roles: ['admin', 'superviseur'] },
      { path: '/espace-enqueteur', label: 'Espace Enquêteur', icon: 'ri-shield-user-line', roles: ['admin','enqueteur'] },
      { path: '/espace-collaborateur', label: 'Espace Collaborateur', icon: 'ri-global-line', roles: ['admin'] },
      { path: '/espace-lecteur', label: 'Espace Lecteur', icon: 'ri-eye-line', roles: ['admin','lecteur'] },
      { path: '/mon-organisme', label: 'Mon Organisme', icon: 'ri-building-2-line', roles: ['admin','agent_declarant', 'collaborateur_national', 'collaborateur_international', 'enqueteur', 'lecteur', 'analyste', 'superviseur'] },
      { path: '/dossiers', label: 'Gestion des Dossiers', icon: 'ri-folder-line', roles: ['admin', 'superviseur', 'analyste', 'enqueteur', 'agent_declarant', 'lecteur'] },
      { path: '/suivi-delais-72h', label: 'Suivi Délais 72h', icon: 'ri-hourglass-line', roles: ['admin', 'superviseur', 'analyste'] },
      { path: '/gestion-documentaire', label: 'Gestion Documentaire', icon: 'ri-file-copy-2-line', roles: ['admin', 'superviseur', 'collaborateur_national', 'collaborateur_international'] },
      { path: '/demandes', label: 'Réquisitions', icon: 'ri-mail-send-line', roles: ['admin', 'superviseur', 'analyste', 'agent_declarant', 'lecteur'] },
      { path: '/echanges-inter-organisations', label: 'Demande d\'information', icon: 'ri-exchange-line', roles: ['admin', 'superviseur', 'collaborateur_national', 'collaborateur_international'] },
      { path: '/suivi-reponses', label: 'Suivi des Réponses', icon: 'ri-reply-all-line', roles: ['admin', 'superviseur', 'analyste', 'agent_declarant', 'collaborateur_national', 'collaborateur_international'] },
      { path: '/parties', label: 'Gestion des Parties', icon: 'ri-group-line', roles: ['admin', 'superviseur', 'analyste'] },
      { path: '/reporting', label: 'Reporting', icon: 'ri-bar-chart-box-line', roles: ['admin', 'superviseur', 'analyste'] },
      { path: '/rapports-dissemination', label: 'Rapports CENTIF', icon: 'ri-share-forward-2-line', roles: ['admin', 'superviseur', 'analyste'] },
      { path: '/declarations-systematiques', label: 'Déclaration Systématique', icon: 'ri-file-list-3-line', roles: ['admin'] },
      { path: '/operation-centif', label: 'Opérations CENTIF', icon: 'ri-database-2-line', roles: ['admin'] },
      { path: '/statistiques-avancees', label: 'Statistiques Avancées', icon: 'ri-line-chart-line', roles: ['admin', 'superviseur'] },
      { path: '/statistiques-delais', label: 'Statistiques Délais', icon: 'ri-timer-line', roles: ['admin', 'superviseur'] },
      { path: '/stats-type-organisation', label: 'Stats par Type', icon: 'ri-pie-chart-2-line', roles: ['admin', 'superviseur'] },
      { path: '/notifications-alertes', label: 'Notifications & Alertes', icon: 'ri-alarm-warning-line', roles: ['admin', 'superviseur', 'analyste', 'enqueteur', 'agent_declarant', 'collaborateur_national', 'collaborateur_international'] },
      { path: '/conformite-organisations', label: 'Conformité Organisations', icon: 'ri-shield-check-line', roles: ['admin', 'superviseur'] },
      { path: '/gestion-utilisateurs', label: 'Gestion Utilisateurs', icon: 'ri-user-settings-line', roles: ['admin'] },
      { path: '/gestion-organisations', label: 'Gestion Organisations', icon: 'ri-building-4-line', roles: ['admin'] },
      { path: '/administration', label: 'Administration', icon: 'ri-settings-3-line', roles: ['admin'] },
      { path: '/parametres', label: 'Paramètres', icon: 'ri-equalizer-2-line', roles: ['admin'] },
      { path: '/newsletters', label: 'Newsletters', icon: 'ri-mail-send-line', roles: ['admin', 'superviseur'] },
      { path: '/guide-utilisateur', label: 'Guide Utilisateur', icon: 'ri-book-open-line', roles: ['admin', 'superviseur', 'analyste', 'enqueteur', 'agent_declarant', 'collaborateur_national', 'collaborateur_international', 'lecteur'] },
    ];

    if (!user?.role) return [];
    return allMenuItems.filter(item => item.roles.includes(user.role));
  };

  const menuItems = getMenuItemsForRole();
  const pinnedItems = menuItems.filter(item => favorites.includes(item.path));
  const regularItems = menuItems.filter(item => !favorites.includes(item.path));

  const handleToggle = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    onCollapse?.(newState);
  };

  useEffect(() => {
    onCollapse?.(isCollapsed);
  }, []);

  const renderMenuItem = (item: typeof menuItems[0], isPinned: boolean = false) => {
    const isActive = location.pathname === item.path;
    const isParametres = item.path === '/parametres' && user?.role === 'admin';
    const isHovered = hoveredItem === item.path;

    return (
      <li key={item.path}>
        <div
          className="relative"
          onMouseEnter={() => setHoveredItem(item.path)}
          onMouseLeave={() => setHoveredItem(null)}
        >
          <Link
            to={item.path}
            className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-4 py-3 rounded-lg text-sm transition-colors cursor-pointer overflow-hidden relative`}
            style={{
              backgroundColor: isActive ? 'var(--accent)' : isPinned ? 'rgba(255,255,255,0.08)' : 'transparent',
              color: theme.text,
              fontWeight: isPinned ? 600 : 500,
            }}
            onMouseEnter={e => {
              if (!isActive) e.currentTarget.style.backgroundColor = theme.hover;
            }}
            onMouseLeave={e => {
              if (!isActive) e.currentTarget.style.backgroundColor = isPinned ? 'rgba(255,255,255,0.08)' : 'transparent';
            }}
            title={isCollapsed ? item.label : ''}
          >
            <div className={`flex items-center ${isCollapsed ? '' : 'space-x-3'}`}>
              <div className="w-5 h-5 flex items-center justify-center relative">
                <i className={`${item.icon} text-lg`}></i>
                {isPinned && isCollapsed && (
                  <div
                    className="absolute -top-1 -right-1 w-2 h-2 rounded-full"
                    style={{ backgroundColor: '#FCD34D' }}
                  ></div>
                )}
              </div>
              {!isCollapsed && <span>{item.label}</span>}
            </div>
            {!isCollapsed && (
              <div className="flex items-center gap-1.5">
                {isPinned && (
                  <i className="ri-star-fill text-sm" style={{ color: '#FCD34D' }}></i>
                )}
                {isParametres && (
                  <kbd
                    className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono font-semibold rounded"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.12)',
                      color: 'rgba(255,255,255,0.7)',
                      border: '1px solid rgba(255,255,255,0.2)',
                    }}
                  >
                    Alt+P
                  </kbd>
                )}
              </div>
            )}
          </Link>
          
          {/* Bouton étoile au survol */}
          {!isCollapsed && isHovered && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleFavorite(item.path);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-md transition-all cursor-pointer z-10"
              style={{
                backgroundColor: 'rgba(0,0,0,0.3)',
                color: isPinned ? '#FCD34D' : 'rgba(255,255,255,0.6)',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.5)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.3)';
              }}
              title={isPinned ? 'Retirer des favoris' : 'Ajouter aux favoris'}
            >
              <i className={`${isPinned ? 'ri-star-fill' : 'ri-star-line'} text-base`}></i>
            </button>
          )}
        </div>
      </li>
    );
  };

  return (
    <>
      <style>{`
        .sidebar-nav::-webkit-scrollbar { width: 4px; }
        .sidebar-nav::-webkit-scrollbar-track { background: ${theme.bg}; }
        .sidebar-nav::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.22); border-radius: 4px; }
        .sidebar-nav::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.38); }
        .sidebar-nav { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.22) ${theme.bg}; }
      `}</style>
      <aside
        className={`${isCollapsed ? 'w-20' : 'w-64'} fixed left-0 top-0 h-screen flex flex-col transition-all duration-300 z-50 overflow-hidden`}
        style={{ backgroundColor: theme.bg }}
      >
        {/* Logo */}
        <div
          className="p-6 flex items-center justify-between"
          style={{ borderBottom: `1px solid ${theme.border}` }}
        >
          {!isCollapsed && (
            <img
              src="/logo.png"
              alt="DOSNET"
              className="h-12 w-auto"
            />
          )}
          <button
            onClick={handleToggle}
            className={`${isCollapsed ? 'mx-auto' : ''} w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer`}
            style={{ color: theme.text }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = theme.hover)}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            title={isCollapsed ? 'Développer' : 'Réduire'}
          >
            <i className={`${isCollapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'} text-xl`}></i>
          </button>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav flex-1 overflow-y-auto overflow-x-hidden py-4">
          {/* Section Favoris */}
          {pinnedItems.length > 0 && (
            <div className="mb-4">
              {!isCollapsed && (
                <div className="px-6 mb-2 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <i className="ri-star-fill text-xs" style={{ color: '#FCD34D' }}></i>
                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: theme.subtext }}>
                      Favoris
                    </span>
                  </div>
                  <button
                    onClick={() => setShowManageFavorites(true)}
                    className="w-6 h-6 flex items-center justify-center rounded transition-colors cursor-pointer"
                    style={{ color: theme.subtext }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = theme.hover)}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    title="Gérer les favoris"
                  >
                    <i className="ri-settings-4-line text-sm"></i>
                  </button>
                </div>
              )}
              <ul className="space-y-1 px-3">
                {pinnedItems.map(item => renderMenuItem(item, true))}
              </ul>
            </div>
          )}

          {/* Séparateur */}
          {pinnedItems.length > 0 && regularItems.length > 0 && (
            <div className="mx-6 mb-4" style={{ borderTop: `1px solid ${theme.border}` }}></div>
          )}

          {/* Section Navigation principale */}
          {regularItems.length > 0 && (
            <div>
              {!isCollapsed && pinnedItems.length > 0 && (
                <div className="px-6 mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: theme.subtext }}>
                    Navigation
                  </span>
                </div>
              )}
              <ul className="space-y-1 px-3">
                {regularItems.map(item => renderMenuItem(item, false))}
              </ul>
            </div>
          )}
        </nav>

        {/* Liens utiles */}
        <div
          className="px-3 py-3"
          style={{ borderTop: `1px solid ${theme.border}` }}
        >
          {!isCollapsed && (
            <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: theme.subtext }}>
              Liens utiles
            </p>
          )}
          <div className={`space-y-1`}>
            {[
              { href: 'https://orifgn.org', label: 'Déclaration Systématique', icon: 'ri-file-list-3-line' }, 
              { href: 'https://centifgn.org', label: 'Site Web CENTIF', icon: 'ri-global-line' },  
            ].map(link => (
              <a
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                title={isCollapsed ? link.label : ''}
                className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer`}
                style={{ color: theme.text }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = theme.hover)}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <i className={`${link.icon} text-lg flex-shrink-0`}></i>
                {!isCollapsed && (
                  <span className="flex-1 truncate" style={{ color: theme.subtext }}>{link.label}</span>
                )}
                {!isCollapsed && (
                  <i className="ri-external-link-line text-xs flex-shrink-0" style={{ color: theme.subtext }}></i>
                )}
              </a>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div
          className="p-4 flex items-center justify-between gap-2"
          style={{ borderTop: `1px solid ${theme.border}` }}
        >
          {!isCollapsed && (
            <div className="text-xs" style={{ color: theme.subtext }}>
              CENTIF Platform v2.5.1
            </div>
          )}
          {/* Bouton aide raccourcis */}
          <button
            onClick={() => {
              window.dispatchEvent(new KeyboardEvent('keydown', { key: '?', altKey: true, bubbles: true }));
            }}
            className={`${isCollapsed ? 'mx-auto' : ''} w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer flex-shrink-0`}
            style={{ color: theme.subtext }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = theme.hover)}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            title="Raccourcis clavier (Alt+?)"
          >
            <i className="ri-keyboard-line text-base"></i>
          </button>
        </div>
      </aside>

      {/* Modal Gérer les favoris */}
      {showManageFavorites && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]"
          onClick={() => setShowManageFavorites(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                  <i className="ri-star-fill text-xl text-yellow-600"></i>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Gérer mes favoris</h2>
                  <p className="text-sm text-gray-500">Sélectionnez les pages à afficher en favoris</p>
                </div>
              </div>
              <button
                onClick={() => setShowManageFavorites(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-500"></i>
              </button>
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-140px)]">
              <div className="space-y-2">
                {menuItems.map(item => {
                  const isFavorite = favorites.includes(item.path);
                  return (
                    <label
                      key={item.path}
                      className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">
                          <i className={`${item.icon} text-lg text-gray-600`}></i>
                        </div>
                        <span className="text-sm font-medium text-gray-900">{item.label}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {isFavorite && (
                          <i className="ri-star-fill text-yellow-500"></i>
                        )}
                        <input
                          type="checkbox"
                          checked={isFavorite}
                          onChange={() => toggleFavorite(item.path)}
                          className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                        />
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
              <div className="text-sm text-gray-600">
                <i className="ri-information-line mr-1"></i>
                {favorites.length} {favorites.length > 1 ? 'favoris sélectionnés' : 'favori sélectionné'}
              </div>
              <button
                onClick={() => setShowManageFavorites(false)}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}